import { createContext, useContext, useState, useEffect } from "react";

const AdminLanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

const ADMIN_LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "et", label: "ET", name: "Eesti" },
  { code: "sv", label: "SV", name: "Svenska" },
];

const TRANSLATIONS = {
  en: {
    // Bookkeeping page
    bk_title: "Bookkeeping",
    bk_subtitle_count: "{docs} documents · {invoices} invoices",
    bk_upload_btn: "Upload + AI analysis",
    bk_analyzing: "AI analyzing...",
    bk_search_placeholder: "Search vendor, customer, invoice number, description...",
    bk_tab_receipts: "Receipts",
    bk_tab_supplier_invoices: "Supplier Invoices",
    bk_tab_customer_invoices: "Customer Invoices",
    bk_tab_other: "Other / Company Docs",
    bk_tab_monthly: "Monthly Overview",
    bk_tab_access: "Accountants",
    bk_no_items: "No {label} found",
    bk_size_kb: "{n} KB",
    bk_label_ai: "AI",
    bk_export_btn: "Export month",
    bk_export_csv: "CSV",
    bk_export_xlsx: "Excel",
    bk_export_pdfs: "All PDFs (ZIP)",
    bk_drop_hint: "Drag & drop files here to upload",
    bk_drop_overlay: "Drop files to upload",
    bk_queue: "{n} file(s) waiting to be processed",
    bk_year: "Year",
    bk_summary_income: "Income",
    bk_summary_expenses: "Expenses",
    bk_summary_vat: "VAT",
    bk_summary_total: "Net result",
    bk_empty_month: "Empty",
    bk_docs_count: "{n} docs",
    bk_invoices_count: "{n} invoices",
    bk_status_paid: "paid",
    bk_status_unpaid: "unpaid",
    bk_create_accountant: "Invite accountant",
    bk_acc_subtitle: "We'll send them an email to set their own password and access the bookkeeping portal.",
    bk_acc_name: "Name",
    bk_acc_email: "Email",
    bk_acc_password: "Password",
    bk_acc_create_btn: "Send invite",
    bk_acc_pending: "PENDING",
    bk_resend_invite: "Resend invite",
    bk_invite_sent: "Invite sent to {email}",
    bk_active_accountants: "ACTIVE ACCOUNTANT ACCOUNTS",
    bk_accountant: "Accountant",
    bk_login_instructions: "LOGIN INSTRUCTIONS",
    bk_login_text: 'Accountants log in at /admin/login with their account. They only see bookkeeping data — documents, receipts and invoices. No access to CRM, customers or projects.',
    bk_confirm_delete_doc: "Delete this document?",
    bk_confirm_delete_acc: "Delete accountant {email}?",
    // Confirm modal
    bk_confirm_title: "Confirm document",
    bk_confirm_subtitle: "AI has extracted the fields below. Adjust if needed and save.",
    bk_field_vendor: "Vendor / Customer",
    bk_field_description: "Description",
    bk_field_category: "Category",
    bk_field_date: "Date",
    bk_field_total: "Total amount",
    bk_field_currency: "Currency",
    bk_field_vat: "VAT amount",
    bk_field_vat_percent: "VAT %",
    bk_save: "Save",
    bk_cancel: "Cancel",
    bk_uploading: "Uploading...",
    bk_ai_failed: "AI analysis failed. You can fill in the fields manually below.",
    bk_save_failed: "Could not save document",
    cat_kvitto: "Receipt",
    cat_leverantörsfaktura: "Supplier invoice",
    cat_kundfaktura: "Customer invoice",
    "cat_löpande kostnad": "Recurring expense",
    cat_skatt: "Tax",
    cat_lön: "Salary",
    cat_övrigt: "Other",
    months: ["January","February","March","April","May","June","July","August","September","October","November","December"],
    // Navigation
    nav_dashboard: "Dashboard",
    nav_leads: "Leads",
    nav_customers: "Customers",
    nav_projects: "Projects",
    nav_articles: "Articles",
    nav_invoices: "Invoices",
    nav_agreements: "Agreements",
    nav_onboarding: "Onboarding",
    nav_outreach: "Outreach",
    nav_prompts: "Prompts",
    nav_bookkeeping: "Bookkeeping",
    nav_support: "Support",
    nav_logout: "Log out",
    // Common
    common_save: "Save",
    common_cancel: "Cancel",
    common_delete: "Delete",
    common_edit: "Edit",
    common_search: "Search",
    common_yes: "Yes",
    common_no: "No",
    common_loading: "Loading...",
    // Invoices page
    inv_title: "Invoices",
    inv_count: "{n} invoices",
    inv_new_btn: "New invoice",
    inv_edit_title: "Edit invoice {id}",
    inv_create_title: "Create invoice",
    inv_search_placeholder: "Search invoice number, customer or amount...",
    inv_export_csv: "Export CSV",
    inv_no_results: "No invoices found",
    // Statuses
    inv_status_all: "All",
    inv_status_draft: "Draft",
    inv_status_sent: "Sent",
    inv_status_paid: "Paid",
    inv_status_overdue: "Overdue",
    inv_status_credited: "Credited",
    inv_status_cancelled: "Cancelled",
    inv_status_unpaid: "Unpaid",
    // Types
    inv_type_all: "All types",
    inv_type_one_time: "One-time",
    inv_type_recurring: "Recurring",
    inv_type_credit_note: "Credit note",
    // Table headers
    inv_col_invoice: "INVOICE",
    inv_col_customer: "CUSTOMER",
    inv_col_type: "TYPE",
    inv_col_amount: "AMOUNT",
    inv_col_status: "STATUS",
    inv_col_due: "DUE DATE",
    inv_col_actions: "ACTIONS",
    // Form
    inv_field_customer: "Customer",
    inv_field_customer_placeholder: "Select customer...",
    inv_field_currency: "Currency",
    inv_field_vat: "VAT",
    inv_field_due: "Due date",
    inv_field_due_in: "Due in {n} days",
    inv_field_interval: "Interval",
    inv_field_monthly: "Monthly",
    inv_field_quarterly: "Quarterly",
    inv_field_yearly: "Yearly",
    inv_line_items: "LINE ITEMS",
    inv_add_from_articles: "+ Add from articles...",
    inv_add_manual: "+ Add manual item",
    inv_col_description: "DESCRIPTION",
    inv_col_qty: "QTY",
    inv_col_unit_price: "UNIT PRICE",
    inv_discount: "DISCOUNT (optional)",
    inv_discount_pct: "%",
    inv_discount_fixed: "Fixed",
    inv_discount_placeholder_pct: "e.g. 10",
    inv_discount_placeholder_amt: "Amount",
    inv_discount_reason: "Reason (optional)",
    inv_subtotal: "Subtotal",
    inv_total: "TOTAL",
    inv_save_changes: "Save changes",
    inv_create_btn: "Create invoice",
    // VAT options
    inv_vat_auto: "VAT: Auto",
    inv_vat_never: "Incl. VAT 24%",
    inv_vat_always: "Reverse VAT",
    inv_vat_no_vat: "Without VAT",
    // Validation errors
    inv_err_customer_required: "Please select a customer.",
    inv_err_no_items: "Add at least one line item.",
    inv_err_item_description: "Line {n}: description is required.",
    inv_err_item_qty: "Line {n}: quantity must be greater than 0.",
    inv_err_item_price: "Line {n}: unit price must be greater than 0.",
    inv_err_zero_total: "Total is 0 — either add price or switch to credit note.",
    // Actions
    inv_action_pdf: "Download PDF",
    inv_action_edit: "Edit draft",
    inv_action_receipt: "Download receipt",
    inv_action_send_receipt: "Email receipt",
    inv_action_credit: "Create credit note",
    inv_action_mark_paid: "Mark as paid",
    inv_action_copy_link: "Copy payment link",
    inv_action_send: "Send invoice",
    inv_action_reminder: "Send reminder",
    inv_action_delete: "Delete invoice",
    // Confirms
    inv_confirm_delete: "Delete invoice {id}? This cannot be undone.",
    inv_confirm_credit: "Create credit note for invoice {id}? This will reverse the full amount ({amount}).",
    inv_confirm_mark_paid: "Mark invoice {id} as paid (bank transfer)?\n\nClick OK to also email a receipt to the customer, or Cancel to just mark it paid silently.",
    inv_link_copied: "Payment link copied.",
    // Send dialog
    inv_send_title: "Send invoice",
    inv_send_reminder_title: "Send payment reminder",
    inv_send_receipt_title: "Send receipt",
    inv_send_recipient: "RECIPIENT EMAIL",
    inv_send_attach_pdf: "Attach PDF invoice",
    inv_send_amount: "Amount",
    inv_send_customer: "Customer",
    inv_send_invoice_id: "Invoice",
    inv_send_btn_invoice: "Send invoice",
    inv_send_btn_reminder: "Send reminder",
    inv_send_btn_receipt: "Send receipt",
    inv_send_sending: "Sending...",
    inv_send_failed: "Could not send",
  },
  et: {
    bk_title: "Raamatupidamine",
    bk_subtitle_count: "{docs} dokumenti · {invoices} arvet",
    bk_upload_btn: "Lae üles + AI analüüs",
    bk_analyzing: "AI analüüsib...",
    bk_search_placeholder: "Otsi tarnijat, klienti, arve numbrit, kirjeldust...",
    bk_tab_receipts: "Tšekid",
    bk_tab_supplier_invoices: "Tarnija arved",
    bk_tab_customer_invoices: "Kliendi arved",
    bk_tab_other: "Muud / Ettevõtte dokumendid",
    bk_tab_monthly: "Kuu ülevaade",
    bk_tab_access: "Raamatupidajad",
    bk_no_items: "Ei leidnud {label}",
    bk_size_kb: "{n} KB",
    bk_label_ai: "AI",
    bk_export_btn: "Ekspordi kuu",
    bk_export_csv: "CSV",
    bk_export_xlsx: "Excel",
    bk_export_pdfs: "Kõik PDF-id (ZIP)",
    bk_drop_hint: "Lohista failid siia üleslaadimiseks",
    bk_drop_overlay: "Lohista failid üleslaadimiseks",
    bk_queue: "{n} faili töötlemise ootel",
    bk_year: "Aasta",
    bk_summary_income: "Sissetulek",
    bk_summary_expenses: "Kulud",
    bk_summary_vat: "KM",
    bk_summary_total: "Netotulemus",
    bk_empty_month: "Tühi",
    bk_docs_count: "{n} dok",
    bk_invoices_count: "{n} arvet",
    bk_status_paid: "tasutud",
    bk_status_unpaid: "tasumata",
    bk_create_accountant: "Kutsu raamatupidaja",
    bk_acc_subtitle: "Saadame talle e-kirja, et ta saaks ise parooli määrata ja portaali ligi pääseda.",
    bk_acc_name: "Nimi",
    bk_acc_email: "E-post",
    bk_acc_password: "Parool",
    bk_acc_create_btn: "Saada kutse",
    bk_acc_pending: "OOTEL",
    bk_resend_invite: "Saada kutse uuesti",
    bk_invite_sent: "Kutse saadetud aadressile {email}",
    bk_active_accountants: "AKTIIVSED RAAMATUPIDAJA KONTOD",
    bk_accountant: "Raamatupidaja",
    bk_login_instructions: "SISSELOGIMISJUHISED",
    bk_login_text: 'Raamatupidajad logivad sisse aadressil /admin/login oma kontoga. Nad näevad ainult raamatupidamise andmeid — dokumente, tšekke ja arveid. Ei pääse ligi CRM-ile, klientidele ega projektidele.',
    bk_confirm_delete_doc: "Kustuta see dokument?",
    bk_confirm_delete_acc: "Kustuta raamatupidaja {email}?",
    bk_confirm_title: "Kinnita dokument",
    bk_confirm_subtitle: "AI on tuvastanud järgmised väljad. Vajadusel kohanda ja salvesta.",
    bk_field_vendor: "Tarnija / Klient",
    bk_field_description: "Kirjeldus",
    bk_field_category: "Kategooria",
    bk_field_date: "Kuupäev",
    bk_field_total: "Kogusumma",
    bk_field_currency: "Valuuta",
    bk_field_vat: "KM summa",
    bk_field_vat_percent: "KM %",
    bk_save: "Salvesta",
    bk_cancel: "Tühista",
    bk_uploading: "Üleslaadimine...",
    bk_ai_failed: "AI analüüs ebaõnnestus. Saad väljad allpool käsitsi täita.",
    bk_save_failed: "Dokumenti ei saanud salvestada",
    cat_kvitto: "Tšekk",
    cat_leverantörsfaktura: "Tarnija arve",
    cat_kundfaktura: "Kliendi arve",
    "cat_löpande kostnad": "Korduv kulu",
    cat_skatt: "Maks",
    cat_lön: "Palk",
    cat_övrigt: "Muu",
    months: ["Jaanuar","Veebruar","Märts","Aprill","Mai","Juuni","Juuli","August","September","Oktoober","November","Detsember"],
  },
  sv: {
    bk_title: "Bokföring",
    bk_subtitle_count: "{docs} dokument · {invoices} fakturor",
    bk_upload_btn: "Ladda upp + AI-analys",
    bk_analyzing: "AI analyserar...",
    bk_search_placeholder: "Sök leverantör, kund, fakturanummer, beskrivning...",
    bk_tab_receipts: "Kvitton",
    bk_tab_supplier_invoices: "Leverantörsfakturor",
    bk_tab_customer_invoices: "Kundfakturor",
    bk_tab_other: "Övrigt / Företagsdokument",
    bk_tab_monthly: "Månadsöversikt",
    bk_tab_access: "Bokförare",
    bk_no_items: "Inga {label} hittades",
    bk_size_kb: "{n} KB",
    bk_label_ai: "AI",
    bk_export_btn: "Exportera månad",
    bk_export_csv: "CSV",
    bk_export_xlsx: "Excel",
    bk_export_pdfs: "Alla PDF:er (ZIP)",
    bk_drop_hint: "Dra och släpp filer här för att ladda upp",
    bk_drop_overlay: "Släpp filer för att ladda upp",
    bk_queue: "{n} fil(er) väntar på att bearbetas",
    bk_year: "År",
    bk_summary_income: "Intäkter",
    bk_summary_expenses: "Utgifter",
    bk_summary_vat: "Moms",
    bk_summary_total: "Nettoresultat",
    bk_empty_month: "Tom",
    bk_docs_count: "{n} dok",
    bk_invoices_count: "{n} fakt",
    bk_status_paid: "betald",
    bk_status_unpaid: "obetald",
    bk_create_accountant: "Bjud in bokförare",
    bk_acc_subtitle: "Vi mejlar dem så att de själva får sätta sitt lösenord och komma åt bokföringen.",
    bk_acc_name: "Namn",
    bk_acc_email: "E-post",
    bk_acc_password: "Lösenord",
    bk_acc_create_btn: "Skicka inbjudan",
    bk_acc_pending: "VÄNTAR",
    bk_resend_invite: "Skicka inbjudan igen",
    bk_invite_sent: "Inbjudan skickad till {email}",
    bk_active_accountants: "AKTIVA BOKFÖRARKONTON",
    bk_accountant: "Bokförare",
    bk_login_instructions: "INLOGGNINGSINSTRUKTIONER",
    bk_login_text: 'Bokföraren loggar in via /admin/login med sitt konto. De ser enbart bokföringsunderlaget — dokument, kvitton och fakturor. Ingen åtkomst till CRM, kunder eller projekt.',
    bk_confirm_delete_doc: "Ta bort detta dokument?",
    bk_confirm_delete_acc: "Ta bort bokförarkonto {email}?",
    bk_confirm_title: "Bekräfta dokument",
    bk_confirm_subtitle: "AI har tolkat fälten nedan. Justera vid behov och spara.",
    bk_field_vendor: "Leverantör / Kund",
    bk_field_description: "Beskrivning",
    bk_field_category: "Kategori",
    bk_field_date: "Datum",
    bk_field_total: "Totalbelopp",
    bk_field_currency: "Valuta",
    bk_field_vat: "Momsbelopp",
    bk_field_vat_percent: "Moms %",
    bk_save: "Spara",
    bk_cancel: "Avbryt",
    bk_uploading: "Laddar upp...",
    bk_ai_failed: "AI-analys misslyckades. Du kan fylla i manuellt nedan.",
    bk_save_failed: "Kunde inte spara dokumentet",
    cat_kvitto: "Kvitto",
    cat_leverantörsfaktura: "Leverantörsfaktura",
    cat_kundfaktura: "Kundfaktura",
    "cat_löpande kostnad": "Löpande kostnad",
    cat_skatt: "Skatt",
    cat_lön: "Lön",
    cat_övrigt: "Övrigt",
    months: ["Januari","Februari","Mars","April","Maj","Juni","Juli","Augusti","September","Oktober","November","December"],
    // Navigation
    nav_dashboard: "Översikt",
    nav_leads: "Leads",
    nav_customers: "Kunder",
    nav_projects: "Projekt",
    nav_articles: "Artiklar",
    nav_invoices: "Fakturor",
    nav_agreements: "Avtal",
    nav_onboarding: "Onboarding",
    nav_outreach: "Prospektering",
    nav_prompts: "AI-prompts",
    nav_bookkeeping: "Bokföring",
    nav_support: "Support",
    nav_logout: "Logga ut",
    // Common
    common_save: "Spara",
    common_cancel: "Avbryt",
    common_delete: "Ta bort",
    common_edit: "Redigera",
    common_search: "Sök",
    common_yes: "Ja",
    common_no: "Nej",
    common_loading: "Laddar...",
    // Invoices page
    inv_title: "Fakturor",
    inv_count: "{n} fakturor",
    inv_new_btn: "Ny faktura",
    inv_edit_title: "Redigera faktura {id}",
    inv_create_title: "Skapa faktura",
    inv_search_placeholder: "Sök fakturanummer, kund eller belopp...",
    inv_export_csv: "Exportera CSV",
    inv_no_results: "Inga fakturor hittades",
    // Statuses
    inv_status_all: "Alla",
    inv_status_draft: "Utkast",
    inv_status_sent: "Skickad",
    inv_status_paid: "Betald",
    inv_status_overdue: "Förfallen",
    inv_status_credited: "Krediterad",
    inv_status_cancelled: "Makulerad",
    inv_status_unpaid: "Obetald",
    // Types
    inv_type_all: "Alla typer",
    inv_type_one_time: "Engångs",
    inv_type_recurring: "Återkommande",
    inv_type_credit_note: "Kreditfaktura",
    // Table headers
    inv_col_invoice: "FAKTURA",
    inv_col_customer: "KUND",
    inv_col_type: "TYP",
    inv_col_amount: "BELOPP",
    inv_col_status: "STATUS",
    inv_col_due: "FÖRFALLER",
    inv_col_actions: "ÅTGÄRDER",
    // Form
    inv_field_customer: "Kund",
    inv_field_customer_placeholder: "Välj kund...",
    inv_field_currency: "Valuta",
    inv_field_vat: "Moms",
    inv_field_due: "Förfallodatum",
    inv_field_due_in: "Förfaller om {n} dagar",
    inv_field_interval: "Intervall",
    inv_field_monthly: "Månadsvis",
    inv_field_quarterly: "Kvartalsvis",
    inv_field_yearly: "Årsvis",
    inv_line_items: "FAKTURARADER",
    inv_add_from_articles: "+ Lägg till från artiklar...",
    inv_add_manual: "+ Lägg till manuell rad",
    inv_col_description: "BESKRIVNING",
    inv_col_qty: "ANT",
    inv_col_unit_price: "STYCKPRIS",
    inv_discount: "RABATT (valfritt)",
    inv_discount_pct: "%",
    inv_discount_fixed: "Fast",
    inv_discount_placeholder_pct: "t.ex. 10",
    inv_discount_placeholder_amt: "Belopp",
    inv_discount_reason: "Anledning (valfritt)",
    inv_subtotal: "Delsumma",
    inv_total: "TOTALT",
    inv_save_changes: "Spara ändringar",
    inv_create_btn: "Skapa faktura",
    // VAT options
    inv_vat_auto: "Moms: Auto",
    inv_vat_never: "Inkl. moms 24%",
    inv_vat_always: "Omvänd moms",
    inv_vat_no_vat: "Utan moms",
    // Validation errors
    inv_err_customer_required: "Välj en kund.",
    inv_err_no_items: "Lägg till minst en fakturarad.",
    inv_err_item_description: "Rad {n}: beskrivning krävs.",
    inv_err_item_qty: "Rad {n}: antalet måste vara större än 0.",
    inv_err_item_price: "Rad {n}: styckpriset måste vara större än 0.",
    inv_err_zero_total: "Totalsumman är 0 — lägg till pris eller växla till kreditfaktura.",
    // Actions
    inv_action_pdf: "Ladda ner PDF",
    inv_action_edit: "Redigera utkast",
    inv_action_receipt: "Ladda ner kvitto",
    inv_action_send_receipt: "Mejla kvitto",
    inv_action_credit: "Skapa kreditfaktura",
    inv_action_mark_paid: "Markera som betald",
    inv_action_copy_link: "Kopiera betallänk",
    inv_action_send: "Skicka faktura",
    inv_action_reminder: "Skicka påminnelse",
    inv_action_delete: "Ta bort faktura",
    // Confirms
    inv_confirm_delete: "Ta bort faktura {id}? Detta kan inte ångras.",
    inv_confirm_credit: "Skapa kreditfaktura för {id}? Hela beloppet ({amount}) återförs.",
    inv_confirm_mark_paid: "Markera faktura {id} som betald (banköverföring)?\n\nKlicka OK för att också mejla kvitto till kunden, eller Avbryt för att bara markera den betald utan mejl.",
    inv_link_copied: "Betallänk kopierad.",
    // Send dialog
    inv_send_title: "Skicka faktura",
    inv_send_reminder_title: "Skicka betalningspåminnelse",
    inv_send_receipt_title: "Skicka kvitto",
    inv_send_recipient: "MOTTAGARE E-POST",
    inv_send_attach_pdf: "Bifoga faktura-PDF",
    inv_send_amount: "Belopp",
    inv_send_customer: "Kund",
    inv_send_invoice_id: "Faktura",
    inv_send_btn_invoice: "Skicka faktura",
    inv_send_btn_reminder: "Skicka påminnelse",
    inv_send_btn_receipt: "Skicka kvitto",
    inv_send_sending: "Skickar...",
    inv_send_failed: "Kunde inte skicka",
  },
};

export function AdminLanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem("wedowedo_admin_lang") || "en"; } catch { return "en"; }
  });

  useEffect(() => {
    try { localStorage.setItem("wedowedo_admin_lang", lang); } catch {}
  }, [lang]);

  const setLang = (code) => {
    if (TRANSLATIONS[code]) setLangState(code);
  };

  const t = (key, vars) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
    let val = dict[key];
    if (val === undefined) val = TRANSLATIONS.en[key];
    if (val === undefined) return key;
    if (typeof val === "string" && vars) {
      Object.entries(vars).forEach(([k, v]) => { val = val.replace(`{${k}}`, v); });
    }
    return val;
  };

  return (
    <AdminLanguageContext.Provider value={{ lang, setLang, t, langs: ADMIN_LANGS }}>
      {children}
    </AdminLanguageContext.Provider>
  );
}

export function useAdminLanguage() {
  return useContext(AdminLanguageContext);
}
